import { Button, Container, Group, Stack } from "@mantine/core"
import { Loader } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import {
  findCertificateRequestsForSupplierQueryKey,
  useFindCertificateRequestsForSupplier,
  useUpdateManyCertificateRequests,
} from "~/gen/api/hooks"
import { useCertificateRequestsBatchModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { useBatchUpdateCertificateRequestsColumns } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/useBatchUpdateCertificateRequestsColumns"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type Props = {
  close: () => void
}

export function BatchUpdateTransactionCertificateRequestsTable({
  close,
}: Props) {
  const { data: certificateRequests, isPending: isLoadingCertificateRequests } =
    useFindCertificateRequestsForSupplier({
      category: "TRANSACTION",
      status: "OPENED",
    })

  const { certificateRequestId, certificateId } =
    useCertificateRequestsBatchModalContext()

  const { mutate: updateManyCertificateRequests, isPending: isUpdating } =
    useUpdateManyCertificateRequests({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: findCertificateRequestsForSupplierQueryKey(),
          })
          setRowSelection({})
          close()
          showSuccessNotification({
            message: t("certificateBatchModal.success", {
              count: checklist.length,
            }),
          })
        },
      },
    })

  const columns = useBatchUpdateCertificateRequestsColumns()
  const [rowSelection, setRowSelection] = useState({})

  const tableData = useMemo(() => {
    return (
      certificateRequests?.data.filter(
        (certificateRequest) => certificateRequest.id !== certificateRequestId
      ) ?? []
    )
  }, [certificateRequests, certificateRequestId])

  const table = useCustomReactTable({
    data: tableData,
    columns,
    state: {
      columnPinning: { left: ["select"] },
      rowSelection,
    },

    enableSorting: false,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
  })

  const checklist = Object.keys(rowSelection)

  const { t } = useTranslation(["traceability", "common"])

  if (isLoadingCertificateRequests) {
    return <Loader fullPage />
  }

  return (
    <Stack>
      <Container mah="80%" m={0} maw="100%">
        <CustomTable table={table} pagination={false} />
      </Container>
      <Group
        justify="center"
        h="10vh"
        pos="sticky"
        bottom={0}
        bg="white"
        w="100%"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <Button
          disabled={isUpdating || checklist.length === 0}
          loading={isUpdating}
          onClick={() => {
            updateManyCertificateRequests({
              data: {
                ids: checklist,
                update: {
                  certificateId: certificateId ?? undefined,
                },
              },
            })
          }}
        >
          {t("batchModal.applyButton", {
            type: t("batchModal.labels.certificate"),
            itemCount: checklist.length,
          })}
        </Button>
      </Group>
    </Stack>
  )
}
